import { ImageEditor } from "./Presentation";
import { AccountAvatar } from "./AccountNavigation";
"use client";

/* eslint-disable @next/next/no-img-element */

import { useEffect, useState } from "react";
import { normaliseSpokenEmail, VoiceTextField } from "./voice";
import { drawingObjectUrl, legacyFetch } from "./legacy-api";
import { api, type ContractorDirectoryRecord } from "./api";

type Project = { id: string; name: string; phase: string; address: string; jobReference: string };
type Contractor = {
  id: string; company: string; trade: string; contactName: string; telephone: string;
  email: string; fullSnagListReadOnly: boolean;
  accountCount: number; initials: string; accessCode: string;
};
type Location = { id: string; name: string };
type Drawing = { id: string; fileName: string; contentType: string; size: number };
type Snag = {
  id: string; reference: string; title: string; description: string; area: string; trade: string;
  priority: string; contractorCompany: string | null; status: string; dueDate: string | null;
  completionNote: string; managerNote: string; createdAt: string; updatedAt: string;
  issuePhotoId: string | null; completionPhotoId: string | null;
};

type SetupData = {
  project: Project;
  projects: Project[];
  contractors: Contractor[];
  locations: Location[];
  drawings: Drawing[];
  snags: Snag[];
};

async function readResult(response: Response) {
  const result = await response.json() as Record<string, unknown> & { error?: string };
  if (!response.ok) throw new Error(result.error || "The change could not be saved");
  return result;
}

export default function ManagerSetup({
  data,
  onRefresh,
  onStartAudit,
}: {
  data: SetupData;
  onRefresh: () => Promise<void>;
  onStartAudit: () => void;
}) {
  const [jobName, setJobName] = useState(data.project.name);
  const [jobPhase, setJobPhase] = useState(data.project.phase);
  const [jobAddress, setJobAddress] = useState(data.project.address);
  const [jobReference, setJobReference] = useState(data.project.jobReference);
  const [locationName, setLocationName] = useState("");
  const [company, setCompany] = useState("");
  const [trade, setTrade] = useState("");
  const [contactName, setContactName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [contractorDirectory, setContractorDirectory] = useState<ContractorDirectoryRecord[]>([]);
  const [drawingFile, setDrawingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    void api.contractorDirectory().then((items) => { if (active) setContractorDirectory(items.filter((item) => item.active)); }).catch(() => undefined);
    return () => { active = false; };
  }, [data.contractors.map((item) => `${item.id}:${item.company}:${item.trade}`).join("|")]);

  function updateCompany(value: string) {
    setCompany(value);
    const known = contractorDirectory.find((item) => item.company.toLocaleLowerCase() === value.trim().toLocaleLowerCase());
    if (!known) return;
    setTrade(known.trade); setContactName(known.contactName); setTelephone(known.telephone); setEmail(known.email);
  }

  async function activateJob(id: string) {
    setBusy("job"); setMessage("");
    try {
      await readResult(await legacyFetch("/api/manager/projects", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, activate: true }),
      }));
      await onRefresh();
      setMessage("Job opened");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not open this job"); }
    finally { setBusy(""); }
  }

  async function saveJob() {
    setBusy("job"); setMessage("");
    try {
      await readResult(await legacyFetch("/api/manager/projects", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: data.project.id, name: jobName, phase: jobPhase, address: jobAddress, jobReference }),
      }));
      await onRefresh();
      setMessage("Job details saved");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not save job details"); }
    finally { setBusy(""); }
  }

  async function addLocation() {
    if (!locationName.trim()) { setMessage("Speak or type a room name first"); return; }
    setBusy("location"); setMessage("");
    try {
      await readResult(await legacyFetch("/api/manager/locations", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: data.project.id, name: locationName }),
      }));
      setLocationName(""); await onRefresh(); setMessage("Room added to the snag dropdown");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not add this room"); }
    finally { setBusy(""); }
  }

  async function addContractor() {
    if (!company.trim() || !trade.trim()) { setMessage("Add the company name and trade"); return; }
    setBusy("contractor"); setMessage("");
    try {
      await readResult(await legacyFetch("/api/manager/contractors", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: data.project.id, company, trade, contactName, telephone, email }),
      }));
      setCompany(""); setTrade(""); setContactName(""); setTelephone(""); setEmail("");
      await onRefresh(); setMessage("Contractor added. Create and share access from the Invitations tab when you are ready.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not add this contractor"); }
    finally { setBusy(""); }
  }

  async function removeContractor(contractor: Contractor) {
    const assigned = data.snags.filter((snag) => snag.contractorCompany === contractor.company).length;
    if (!window.confirm(`Remove ${contractor.company} from this project?${assigned ? ` ${assigned} live snag${assigned === 1 ? "" : "s"} will become unassigned.` : ""} Existing accounts keep their company profile but will no longer have access to this job.`)) return;
    setBusy(`remove-${contractor.id}`); setMessage("");
    try {
      await readResult(await legacyFetch("/api/manager/contractors", {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: data.project.id, contractorCompanyId: contractor.id }),
      }));
      await onRefresh(); setMessage(`${contractor.company} removed from this project`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not remove this contractor"); }
    finally { setBusy(""); }
  }

  async function uploadDrawing(file: File) {
    setBusy("drawing"); setMessage("");
    try {
      const form = new FormData();
      form.append("projectId", String(data.project.id));
      form.append("drawing", file);
      await readResult(await legacyFetch("/api/manager/drawings", { method: "POST", body: form }));
      setDrawingFile(null); await onRefresh(); setMessage("Drawing uploaded. It will open automatically during the next snag.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not upload this drawing"); }
    finally { setBusy(""); }
  }

  return <section className="setup-page">
    <div className="setup-title-row">
      <div><p className="eyebrow">MANAGER ONLY</p><h1>Set up a job</h1><p>Create the job once, then the rooms and contractors appear as simple dropdowns during every snag.</p></div>
      <button className="primary-button" onClick={onStartAudit} type="button"><span className="button-icon" aria-hidden="true">＋</span> Start site snag</button>
    </div>

    <div className="setup-progress" aria-label="Setup steps">
      {[['job', 'Job', true], ['rooms', 'Rooms', Boolean(data.locations.length)], ['contractors', 'Contractors', Boolean(data.contractors.length)], ['drawing', 'Drawing', Boolean(data.drawings.length)]].map(([id, label, complete], index) => <button type="button" key={String(id)} className={complete ? 'done' : ''} onClick={() => { const section = document.getElementById('setup-' + id); section?.scrollIntoView({ behavior: 'smooth', block: 'start' }); section?.focus({ preventScroll: true }); }}>{complete ? '✓' : index + 1} <b>{label}</b>{id === 'drawing' && <small>optional</small>}</button>)}
    </div>

    {message && <div className="setup-message">{message}</div>}

    <div className="setup-grid">
      <article id="setup-job" tabIndex={-1} className="setup-card job-card">
        <ImageEditor key={data.project.id} kind="project" id={data.project.id} label="Project photograph" fallback="▦" />
        <header><span>1</span><div><h2>Job details</h2><p>Open an existing job or create a completely blank one.</p></div></header>
        <label className="plain-select">Active job<select value={data.project.id} disabled={busy === "job"} onChange={(event) => void activateJob(event.target.value)}>{data.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>
        <div className="setup-form">
          <VoiceTextField label="Job name" value={jobName} onChange={setJobName} placeholder="Example: Abbey Junior School" />
          <VoiceTextField label="Job reference" value={jobReference} onChange={setJobReference} placeholder="Example: RH-1042" />
          <VoiceTextField label="Phase or work package" value={jobPhase} onChange={setJobPhase} placeholder="Example: Summer refurbishment" />
          <VoiceTextField label="Address" value={jobAddress} onChange={setJobAddress} placeholder="Type or speak the site address" />
          <button className="setup-save" disabled={busy === "job" || !jobName.trim()} onClick={() => void saveJob()} type="button">{busy === "job" ? "Saving…" : "Save job details"}</button>
        </div>
      </article>

      <article id="setup-rooms" tabIndex={-1} className="setup-card">
        <header><span>2</span><div><h2>Rooms and locations</h2><p>These become the location dropdown in the snag screen.</p></div></header>
        <div className="setup-form inline-add-form">
          <VoiceTextField label="Add a room or area" value={locationName} onChange={setLocationName} placeholder="Example: First floor · Room 14" />
          <button className="setup-save" disabled={busy === "location" || !locationName.trim()} onClick={() => void addLocation()} type="button">{busy === "location" ? "Adding…" : "Add to dropdown"}</button>
        </div>
        <div className="location-list">{data.locations.map((location) => <span key={location.id}>⌖ {location.name}</span>)}{!data.locations.length && <p>No rooms added yet.</p>}</div>
      </article>

      <article id="setup-contractors" tabIndex={-1} className="setup-card">
        <header><span>3</span><div><h2>Contractors</h2><p>Add each company once so it appears in the assignment dropdown.</p></div></header>
        <div className="setup-form contractor-add-form">
          <label className="plain-select existing-company-select">Existing companies<select value="" onChange={(event) => updateCompany(event.target.value)}><option value="">Choose a saved company</option>{contractorDirectory.map((item) => <option key={item.id} value={item.company}>{item.company} · {item.trade}</option>)}</select></label>
          <VoiceTextField label="Company name" list="known-contractor-companies" value={company} onChange={updateCompany} placeholder="Type a new company name" required />
          <datalist id="known-contractor-companies">{contractorDirectory.map((item) => <option key={item.id} value={item.company}>{item.trade}</option>)}</datalist>
          <VoiceTextField label="Trade" value={trade} onChange={setTrade} placeholder="Example: Joinery" required />
          <VoiceTextField label="Contact name" value={contactName} onChange={setContactName} placeholder="Optional" />
          <VoiceTextField inputMode="tel" label="Telephone" value={telephone} onChange={setTelephone} placeholder="Optional" type="tel" />
          <VoiceTextField inputMode="email" label="Contractor email" value={email} onChange={setEmail} placeholder="Optional · name@company.co.uk" speechTransform={normaliseSpokenEmail} type="email" />
          <p className="contractor-required-note">Company name and trade are the only required details.</p>
          <button className="setup-save" disabled={busy === "contractor" || !company.trim() || !trade.trim()} onClick={() => void addContractor()} type="button">{busy === "contractor" ? "Adding…" : "Add contractor"}</button>
        </div>
        <div className="setup-contractor-list">{data.contractors.map((contractor) => <div className="setup-company-row" key={contractor.id}><AccountAvatar name={contractor.company} initials={contractor.initials} companyId={contractor.id} /><div className="setup-company-details"><strong>{contractor.company}</strong><small>{contractor.trade}{contractor.contactName ? ` · ${contractor.contactName}` : ""}{contractor.telephone ? ` · ${contractor.telephone}` : ""}{contractor.email ? ` · ${contractor.email}` : ""}</small></div><span className="setup-company-accounts">{contractor.accountCount} active account{contractor.accountCount === 1 ? "" : "s"}</span><button disabled={busy === `remove-${contractor.id}`} onClick={() => void removeContractor(contractor)} type="button">{busy === `remove-${contractor.id}` ? "Removing…" : "Remove"}</button></div>)}{!data.contractors.length && <p>No contractors added yet.</p>}</div>
      </article>

      <article id="setup-drawing" tabIndex={-1} className="setup-card drawing-setup-card">
        <header><span>4</span><div><h2>Drawings <small>optional</small></h2><p>Upload a plan. During an snag you can select it and tap the defect location to place a reference pin.</p></div></header>
        <div className="drawing-upload-row auto-upload">
          <label className={busy === "drawing" ? "uploading" : ""}><b>{busy === "drawing" ? `Uploading ${drawingFile?.name || "drawing"}…` : "Choose a drawing to upload"}</b><small>{busy === "drawing" ? "Please keep this page open" : "Uploads automatically · PDF, JPG, PNG or WebP · maximum 20 MB"}</small><input accept="application/pdf,image/jpeg,image/png,image/webp" disabled={busy === "drawing"} type="file" onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            event.currentTarget.value = "";
            setDrawingFile(file);
            if (file) void uploadDrawing(file);
          }} /></label>
        </div>
        <div className="drawing-list">{data.drawings.map((drawing) => <DrawingLink drawing={drawing} key={drawing.id} />)}{!data.drawings.length && <p>No drawing uploaded. You can skip this step.</p>}</div>
      </article>

      <article className="setup-card start-audit-card"><button className="create-project-toggle" onClick={onStartAudit} type="button"><span>＋</span><div><p className="eyebrow">SETUP COMPLETE</p><h2>Start the site snag</h2><p>Open the camera and begin adding snags to this project.</p></div><b>Start →</b></button></article>
    </div>
  </section>;
}

function DrawingLink({ drawing }: { drawing: Drawing }) {
  const [url, setUrl] = useState("");
  useEffect(() => { let active = true; void drawingObjectUrl(drawing.id).then((value) => { if (active) setUrl(value); }); return () => { active = false; }; }, [drawing.id]);
  return <a href={url || undefined} aria-disabled={!url} rel="noreferrer" target="_blank">
    {drawing.contentType.startsWith("image/") && url ? <img src={url} alt="" /> : <span>PDF</span>}
    <div><strong>{drawing.fileName}</strong><small>{Math.max(1, Math.round(drawing.size / 1024))} KB · ready to mark</small></div><b>View ↗</b>
  </a>;
}
